import * as React from "react";
import { makeStyles, shorthands } from "@fluentui/react-components";
import { REFINED } from "../../risikoanalyseStep/tokens";
import { useFormPrimitiveStyles } from "../formPrimitives";

/**
 * Tag-/Chip-Eingabe (z. B. Mitgliedschaften). Tippen + Enter (oder Komma) legt
 * einen Chip an, „×" entfernt, Backspace bei leerem Feld entfernt den letzten.
 * Persistiert als EIN String, mit „; " verbunden (Container sieht aus wie ein
 * normales REFINED-Eingabefeld).
 */

const SEP = "; ";

const useLocal = makeStyles({
  box: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    columnGap: "6px",
    rowGap: "6px",
    minHeight: "44px",
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("10px"),
    ...shorthands.padding("7px", "10px"),
    backgroundColor: REFINED.white,
    cursor: "text",
    ":focus-within": { ...shorthands.borderColor(REFINED.aub), boxShadow: REFINED.focusRing },
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "4px",
    ...shorthands.padding("3px", "4px", "3px", "10px"),
    ...shorthands.borderRadius("999px"),
    backgroundColor: REFINED.aub50,
    color: REFINED.aub,
    fontFamily: REFINED.fontUI,
    fontSize: "0.84rem",
    fontWeight: 600,
  },
  x: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "18px",
    height: "18px",
    ...shorthands.borderWidth("0"),
    ...shorthands.borderRadius("999px"),
    backgroundColor: "transparent",
    color: REFINED.aub,
    cursor: "pointer",
    fontSize: "0.95rem",
    lineHeight: "1",
    ":hover": { backgroundColor: REFINED.aub100 },
  },
  input: {
    flexGrow: 1,
    minWidth: "140px",
    ...shorthands.borderWidth("0"),
    ...shorthands.padding("4px", "0"),
    outlineStyle: "none",
    backgroundColor: "transparent",
    fontFamily: REFINED.fontUI,
    fontSize: "0.95rem",
    color: REFINED.ink,
    "::placeholder": { color: REFINED.ink3 },
  },
});

export function TagInput({
  label,
  name,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  name: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  const s = useFormPrimitiveStyles();
  const l = useLocal();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [draft, setDraft] = React.useState("");

  const tags = React.useMemo(
    () => (value ? value.split(/[;,]/).map((t) => t.trim()).filter(Boolean) : []),
    [value],
  );

  const commit = (next: string[]) => onChange(next.join(SEP));
  const add = (raw: string) => {
    const t = raw.trim();
    setDraft("");
    if (!t || tags.includes(t)) return;
    commit([...tags, t]);
  };
  const removeAt = (i: number) => commit(tags.filter((_, idx) => idx !== i));

  return (
    <div className={s.field}>
      <label className={s.label} htmlFor={name}>{label}</label>
      <div className={l.box} onClick={() => inputRef.current?.focus()}>
        {tags.map((t, i) => (
          <span key={`${t}-${i}`} className={l.chip}>
            {t}
            <button
              type="button"
              className={l.x}
              aria-label={`${t} entfernen`}
              onClick={(e) => {
                e.stopPropagation();
                removeAt(i);
              }}
            >
              ×
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          id={name}
          name={name}
          className={l.input}
          value={draft}
          placeholder={tags.length ? "" : placeholder}
          onChange={(e) => {
            const v = e.target.value;
            if (v.includes(",")) v.split(",").forEach(add);
            else setDraft(v);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              add(draft);
            } else if (e.key === "Backspace" && !draft && tags.length) {
              removeAt(tags.length - 1);
            }
          }}
          onBlur={() => {
            if (draft.trim()) add(draft);
          }}
        />
      </div>
    </div>
  );
}

export default TagInput;
